
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePallet } from "../../../contexts/PalletContext";
import * as httpHelper from '../../../helpers/http';
import Paper from "@mui/material/Paper";
import { isMobile } from "react-device-detect";
import TabPanel from "../../../components/carousel/TabPanel";
import Grid from "@mui/material/Grid";
import ButtonIconSalvar from "../../../components/ButtonIconSalvar";
import EmptyError from "../../../components/EmptyError";
import { Avatar, Box, Button, Chip, CircularProgress, InputAdornment, Link, TextField, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAuthorization } from '../../../contexts/AuthorizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import SearchIcon from '@mui/icons-material/Search';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LinkIcon from '@mui/icons-material/Link';
import HistoryIcon from '@mui/icons-material/History';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PolicyIcon from '@mui/icons-material/Policy';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';

interface AutorVoto { nome: string; tipo?: string; id_parlamentar?: number | null; }
interface TendenciaItem { tema?: string; titulo?: string; nome?: string; count?: number; autores?: AutorVoto[]; autoresTotal?: number; tipo?: string; papel?: string; comissao?: string | null; categoria?: 'favoravel' | 'contrario' | 'autoria' | 'relatoria'; }

interface Perfil {
    foto: string;
    nome_completo: string;
    apelido: string;
    partido: { sigla: string; motivo: string };
    formacao_profissional: {
        profissao: string;
        formacoes: { curso: string; instituicao: string; motivo: string }[];
    };
    contatos: {
        diretos: { tipo: string; detalhe: string }[];
        indiretos: { tipo: string; detalhe: string }[];
    };
    financiadores: { nome: string; quantia: string; origem: string }[];
    ocorrencias_juridicas: { tipo: string; detalhe: string }[];
    temas_de_atuacao: { defende: string[]; ataca: string[] };
    historico_politico: { ano: number; cargo: string; votos: number; eleitorado: string }[];
    projetos: {
        principais?: { nome: string; descricao: string }[];
        propostos?: { nome: string; descricao: string }[];
        participacoes: { nome: string; descricao: string }[];
    };
    tendencias_votacao?: {
        resumo: string;
        favoravel: TendenciaItem[];
        contrario: TendenciaItem[];
        autorias?: TendenciaItem[];
        relatorias?: TendenciaItem[];
    };
    fontes: string[];
    rede?: {
        centro: { nome: string; foto?: string | null };
        nos: { nome: string; id_parlamentar: number | null; tipo: string; peso: number; projetos: string[]; motivo: string; foto?: string | null }[];
        arestas?: { a: number; b: number; peso: number }[];
    };
}

const C = {
    dark: '#1E0736',
    primary: '#3B0675',
    medium: '#4B1196',
    lavender: '#dbcfea',
    cardBg: '#fff',
    cardShadow: '0 2px 12px rgba(59, 6, 117, 0.06)',
    cardBorder: '#ede7f6',
    headerBg: 'rgba(75, 17, 150, 0.04)',
    green: '#2e7d32',
    red: '#c62828',
    amber: '#e65100',
};

const SectionCard = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
    <Box sx={{ backgroundColor: C.cardBg, borderRadius: '12px', boxShadow: C.cardShadow, border: `1px solid ${C.cardBorder}`, overflow: 'hidden', mb: 2, breakInside: 'avoid' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, backgroundColor: C.headerBg, borderBottom: `1px solid ${C.cardBorder}` }}>
            <Box sx={{ color: C.medium, display: 'flex' }}>{icon}</Box>
            <Typography sx={{ fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.72rem' }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>{children}</Box>
    </Box>
);

const getContactIcon = (tipo: string) => {
    const t = tipo.toLowerCase();
    if (t.includes('email') || t.includes('e-mail')) return <EmailIcon fontSize="small" />;
    if (t.includes('tel') || t.includes('fone') || t.includes('whatsapp') || t.includes('cel')) return <PhoneIcon fontSize="small" />;
    if (t.includes('end') || t.includes('local')) return <LocationOnIcon fontSize="small" />;
    return <ContactPhoneIcon fontSize="small" />;
};

const ParlamentarCarouselTabsPerfil = (props: {
    id_parlamentar: number;
    activeTab: any;
    index: number;
    loading?: boolean;
    permissaoUsuario?: number;
    parlamentar?: any;
}) => {
    const { pallet } = usePallet();
    const pdfRef = useRef<HTMLDivElement>(null);
    const conteudoRef = useRef<HTMLDivElement>(null);
    const [votosFiltro, setVotosFiltro] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState<'todos' | 'favoravel' | 'contrario' | 'autoria' | 'relatoria'>('todos');
    const [expandido, setExpandido] = useState(false);
    const [redeHover, setRedeHover] = useState<number | null>(null);

    // ---- Gerar Perfil IA (disparo manual da fila searchParlamentarProfile) ----
    const { getConfig } = useAuthorization();
    const { NewAlert } = useAlert();
    const [gerando, setGerando] = useState(false);
    const pollRef = useRef<any>(null);

    // Gate do BOTÃO: habIa && habPerfil. Se o kill-switch de crédito desligar HAB_IA, o botão
    // some sozinho (mesma regra dos demais agentes de IA). NÃO afeta a exibição do perfil.
    const habPerfilAtivo = !!(getConfig()?.habIa && getConfig()?.habPerfil);

    const pararPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const consultarProcessando = async (): Promise<boolean> => {
        const id = props.parlamentar?.id;
        if (!id) return false;
        const resp = await httpHelper.get(`/parlamentar/status-process-ia/${id}`);
        return !!resp?.data?.processamento_ia;
    };

    const iniciarPolling = () => {
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            try {
                const processando = await consultarProcessando();
                if (!processando) {
                    pararPolling();
                    setGerando(false);
                    NewAlert('success', 'Perfil gerado com sucesso!');
                    // Recarrega para renderizar o perfil recém-gerado (o parlamentar vem por prop do pai).
                    setTimeout(() => window.location.reload(), 1200);
                }
            } catch {
                // Erro transitório de rede: mantém o polling (não derruba a percepção de progresso).
            }
        }, 5000);
    };

    const handleGerarPerfil = async () => {
        if (gerando) return;
        const id = props.parlamentar?.id;
        if (!id) return;
        setGerando(true);
        try {
            const resp = await httpHelper.post(`/parlamentar/gerar-perfil-ia/${id}`);
            if (resp?.success && resp?.data?.enfileirado) {
                NewAlert('success', 'Geração de perfil iniciada. Isso pode levar alguns minutos.');
                iniciarPolling();
            } else {
                setGerando(false);
                NewAlert('error', resp?.data?.motivo || 'Não foi possível iniciar a geração do perfil.');
            }
        } catch {
            setGerando(false);
            NewAlert('error', 'Falha ao iniciar a geração do perfil.');
        }
    };

    // Se a página abrir com uma geração já em andamento (ex.: F5 no meio), reflete o loader e
    // retoma o polling — sem isto o usuário veria o botão liberado durante um processamento vivo.
    useEffect(() => {
        if (!habPerfilAtivo) return;
        let cancelado = false;
        (async () => {
            try {
                const processando = await consultarProcessando();
                if (!cancelado && processando) { setGerando(true); iniciarPolling(); }
            } catch { /* ignora */ }
        })();
        return () => { cancelado = true; pararPolling(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.parlamentar?.id, habPerfilAtivo]);

    useEffect(() => {
        const handleClickFora = (e: MouseEvent) => {
            if (conteudoRef.current && !conteudoRef.current.contains(e.target as Node)) {
                setExpandido(false);
            }
        };
        document.addEventListener('mousedown', handleClickFora);
        return () => document.removeEventListener('mousedown', handleClickFora);
    }, []);

    // Normaliza para busca insensível a acento e caixa
    const normalizar = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');


    // Linha de autoria (sempre visível, truncada) + "bandeirinha" no hover com a lista completa.
    const renderAutoria = (item: TendenciaItem) => {
        const autores = item.autores ?? [];
        if (!autores.length) return null;
        const total = item.autoresTotal ?? autores.length;
        const restante = Math.max(0, total - autores.length);
        const resumo = autores.map(a => a.nome).join(' · ') + (restante > 0 ? ` +${restante}` : '');

        const conteudoTooltip = (
            <Box sx={{ p: 0.25 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, mb: 0.5 }}>
                    Autoria{total > 1 ? ` (${total})` : ''}
                </Typography>
                {autores.map((a, idx) => (
                    <Typography key={idx} sx={{ fontSize: '0.72rem', lineHeight: 1.5 }}>
                        • {a.nome}{a.tipo ? ` — ${a.tipo}` : ''}
                    </Typography>
                ))}
                {restante > 0 && (
                    <Typography sx={{ fontSize: '0.72rem', fontStyle: 'italic', mt: 0.25 }}>
                        …e outros {restante}
                    </Typography>
                )}
            </Box>
        );

        return (
            <Tooltip title={conteudoTooltip} arrow placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, cursor: 'default', minWidth: 0 }}>
                    <GroupIcon sx={{ fontSize: 13, color: C.medium, flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: '0.68rem', color: C.medium, fontWeight: 600 }}>
                        {resumo}
                    </Typography>
                </Box>
            </Tooltip>
        );
    };

    // Rede de proximidade (Fase 1): autores dos projetos que o parlamentar votou a favor.
    // Layout radial próprio (sem lib de grafo): linhas em SVG ao fundo, nós em HTML com Tooltip.
    const renderRede = () => {
        const rede = perfilData?.rede;
        if (!rede || !rede.nos?.length) return null;
        const nos = rede.nos;
        const arestas = rede.arestas || [];
        const maxPeso = Math.max(...nos.map(n => n.peso), 1);
        const maxPesoAresta = Math.max(...arestas.map(e => e.peso), 1);
        const ehEntidade = (t?: string) => !!t && t !== 'Parlamentar';
        const inicial = (n?: string) => (n || '?').trim().charAt(0).toUpperCase();

        const anelInterno = Math.min(8, nos.length);
        const pos = nos.map((n, i) => {
            const interno = i < anelInterno;
            const idx = interno ? i : i - anelInterno;
            const total = interno ? anelInterno : (nos.length - anelInterno);
            const Rx = interno ? 26 : 41;
            const Ry = interno ? 28 : 43;
            const ang = (idx / Math.max(total, 1)) * 2 * Math.PI - Math.PI / 2 + (interno ? 0 : Math.PI / Math.max(total, 1));
            return {
                ...n,
                left: 50 + Rx * Math.cos(ang),
                top: 50 + Ry * Math.sin(ang),
                size: 34 + Math.round(26 * (n.peso / maxPeso)),
            };
        });

        // Adjacência para o realce no hover (nós ligados por coautoria).
        const adj = new Map<number, Set<number>>();
        arestas.forEach(e => {
            if (!adj.has(e.a)) adj.set(e.a, new Set());
            if (!adj.has(e.b)) adj.set(e.b, new Set());
            adj.get(e.a)!.add(e.b); adj.get(e.b)!.add(e.a);
        });
        const conectados = redeHover != null ? (adj.get(redeHover) || new Set<number>()) : new Set<number>();
        const noAtivo = (i: number) => redeHover == null || i === redeHover || conectados.has(i);

        const alturaRede = Math.min(560, Math.max(340, 220 + nos.length * 12));

        return (
            <SectionCard icon={<GroupIcon fontSize="small" />} title="Rede de Proximidade">
                <Typography sx={{ fontSize: '0.72rem', color: '#666', mb: 1.5 }}>
                    {rede.centro?.nome} ao centro; ao redor, os autores dos projetos que ele votou a favor (tamanho do nó = nº de projetos apoiados). As linhas entre os nós indicam coautoria em comum. Passe o mouse num nó para ver suas conexões.
                </Typography>
                <Box sx={{ position: 'relative', width: '100%', height: alturaRede, overflow: 'hidden' }}>
                    <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        {/* centro -> nós */}
                        {pos.map((p, i) => (
                            <line key={`c${i}`} x1="50%" y1="50%" x2={`${p.left}%`} y2={`${p.top}%`}
                                stroke={C.lavender} strokeWidth={1}
                                style={{ opacity: noAtivo(i) ? 0.6 : 0.08, transition: 'opacity .15s' }} />
                        ))}
                        {/* nó <-> nó (coautoria) */}
                        {arestas.map((e, i) => {
                            if (!pos[e.a] || !pos[e.b]) return null;
                            const ativo = redeHover != null && (e.a === redeHover || e.b === redeHover);
                            const visivel = redeHover == null || ativo;
                            return (
                                <line key={`a${i}`}
                                    x1={`${pos[e.a].left}%`} y1={`${pos[e.a].top}%`}
                                    x2={`${pos[e.b].left}%`} y2={`${pos[e.b].top}%`}
                                    stroke={ativo ? C.medium : '#b9aee0'}
                                    strokeWidth={0.6 + 1.8 * (e.peso / maxPesoAresta)}
                                    style={{ opacity: visivel ? (ativo ? 0.9 : 0.32) : 0.05, transition: 'opacity .15s' }} />
                            );
                        })}
                    </svg>

                    {/* centro */}
                    <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', zIndex: 3 }}>
                        <Avatar src={rede.centro?.foto || undefined} sx={{ width: 64, height: 64, border: `3px solid ${C.primary}`, m: '0 auto', boxShadow: '0 2px 8px rgba(59,6,117,0.35)' }}>
                            {inicial(rede.centro?.nome)}
                        </Avatar>
                        <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.dark, mt: 0.5, maxWidth: 120 }}>
                            {rede.centro?.nome}
                        </Typography>
                    </Box>

                    {/* nós */}
                    {pos.map((p, i) => {
                        const entidade = ehEntidade(p.tipo);
                        const ativo = noAtivo(i);
                        const tip = (
                            <Box sx={{ p: 0.25 }}>
                                <Typography sx={{ fontSize: '0.74rem', fontWeight: 700 }}>{p.nome}</Typography>
                                <Typography sx={{ fontSize: '0.7rem', opacity: 0.9 }}>{p.tipo}</Typography>
                                <Typography sx={{ fontSize: '0.7rem', mt: 0.5 }}>{p.motivo}</Typography>
                                {p.projetos?.length > 0 && (
                                    <Typography sx={{ fontSize: '0.68rem', mt: 0.25, opacity: 0.85 }}>
                                        {p.projetos.join(' · ')}
                                    </Typography>
                                )}
                            </Box>
                        );
                        return (
                            <Tooltip key={i} title={tip} arrow placement="top">
                                <Box
                                    onMouseEnter={() => setRedeHover(i)}
                                    onMouseLeave={() => setRedeHover(null)}
                                    sx={{
                                        position: 'absolute', left: `${p.left}%`, top: `${p.top}%`,
                                        transform: 'translate(-50%,-50%)', zIndex: 2, cursor: 'pointer',
                                        opacity: ativo ? 1 : 0.18, transition: 'opacity .15s',
                                    }}>
                                    <Box sx={{ position: 'relative', width: p.size, height: p.size }}>
                                        <Avatar
                                            variant={entidade ? 'rounded' : 'circular'}
                                            src={p.foto || undefined}
                                            sx={{
                                                width: p.size, height: p.size,
                                                fontSize: Math.round(p.size * 0.4),
                                                bgcolor: entidade ? '#8d8d8d' : C.primary,
                                                border: `2px solid ${redeHover === i ? C.medium : '#fff'}`,
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.28)',
                                            }}>
                                            {inicial(p.nome)}
                                        </Avatar>
                                        <Box sx={{
                                            position: 'absolute', bottom: -2, right: -2,
                                            minWidth: 15, height: 15, px: '3px', borderRadius: '8px',
                                            backgroundColor: C.dark, color: '#fff', fontSize: '0.58rem', fontWeight: 700,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fff',
                                        }}>
                                            {p.peso}
                                        </Box>
                                    </Box>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </SectionCard>
        );
    };

    const perfilData = useMemo<Perfil | null>(() => {
        const raw = props.parlamentar?.perfil;
        if (!raw) return null;
        if (typeof raw !== 'string') return raw as Perfil;
        let cleaned = raw
            .replace(/(\d)\.(?=\d{3}(\D|$))/g, '$1')
            .replace(/^```(?:json)?\s*/, '')
            .replace(/```$/, '')
            .trim();
        try { return JSON.parse(cleaned) as Perfil; }
        catch (err) { console.error('Falha ao JSON.parse do perfil:', err); return null; }
    }, [props.parlamentar?.perfil]);

    const acessarPerfilTSE = () => {
        const url = props.parlamentar?.divulgacandcontas_url;
        if (!url) return;
        const largura = Math.floor(window.screen.availWidth * 0.8);
        const altura = Math.floor(window.screen.availHeight * 0.8);
        const left = Math.floor((window.screen.availWidth - largura) / 2);
        const top = Math.floor((window.screen.availHeight - altura) / 2);
        const win = window.open(url, '_blank', `width=${largura},height=${altura},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`);
        if (win) win.focus();
        else alert('Popup bloqueado. Habilite pop-ups para este site.');
    };

    const handleDownloadPdf = async () => {
        if (!pdfRef.current) return;

        // Busca imagens externas via proxy autenticado do backend (contorna CORS)
        const imgEls = Array.from(pdfRef.current.querySelectorAll('img'));
        const restored: { el: HTMLImageElement; src: string }[] = [];

        await Promise.all(
            imgEls.map(async (img) => {
                const src = img.getAttribute('src') || '';
                if (!src || src.startsWith('data:')) return;
                try {
                    const resp = await httpHelper.get(
                        `/parlamentar/proxy/imagem?url=${encodeURIComponent(src)}`
                    );
                    if (resp.success && resp.data?.data) {
                        restored.push({ el: img, src });
                        img.src = resp.data.data;
                    }
                } catch {
                    // proxy indisponível "” html2canvas vai omitir a imagem
                }
            })
        );

        const canvas = await html2canvas(pdfRef.current, {
            scale: 2,
            useCORS: false,
            allowTaint: true,
            logging: false,
        });

        restored.forEach(({ el, src }) => { el.src = src; });

        const imgData = canvas.toDataURL('image/png');

        // Página única com altura exata do conteúdo "” sem corte entre páginas
        const pdfWidthMm = 210;
        const pdfHeightMm = (canvas.height * pdfWidthMm) / canvas.width;

        const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [pdfWidthMm, pdfHeightMm] });
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidthMm, pdfHeightMm);
        pdf.save(`perfil-parlamentar-${props.parlamentar?.nome}.pdf`);
    };

    const mainProjects = perfilData?.projetos?.principais ?? perfilData?.projetos?.propostos ?? [];

    return (
        <div>
            <Paper style={{ borderRadius: '6px', marginTop: '0px' }}>
                <TabPanel value={props.activeTab} index={props.index}>

                    {/* Action bar */}
                    <Grid container justifyContent="flex-end" alignItems="center" style={{ backgroundColor: pallet.backgroundColorTertiary, border: '1px solid #EAEAEA', padding: '16px 20px' }}>
                        {habPerfilAtivo && (
                            <Grid style={{ marginRight: '10px' }}>
                                <ButtonIconSalvar
                                    onClick={handleGerarPerfil}
                                    tipo="gerarPerfilIA"
                                    backgroundColor={C.primary}
                                    border="1px solid lightgrey"
                                    fontColor="white"
                                    fontSize={pallet.general.size12}
                                    icon={gerando
                                        ? <CircularProgress size={14} style={{ color: 'white' }} />
                                        : <AutoAwesomeIcon style={{ fontSize: 16 }} />}
                                    title={gerando ? 'Gerando perfil…' : 'Gerar Perfil IA'}
                                    disable={gerando}
                                />
                            </Grid>
                        )}
                        {props.parlamentar?.perfil && (
                            <Grid style={{ marginRight: '10px' }}>
                                <ButtonIconSalvar
                                    onClick={handleDownloadPdf}
                                    tipo="baixarPDF"
                                    backgroundColor={C.primary}
                                    border="1px solid lightgrey"
                                    fontColor="white"
                                    fontSize={pallet.general.size12}
                                    icon=""
                                    title="Baixar PDF"
                                />
                            </Grid>
                        )}
                        <ButtonIconSalvar
                            onClick={acessarPerfilTSE}
                            tipo="outrosevento"
                            backgroundColor={pallet.backgroundColorTertiary}
                            border="1px solid lightgrey"
                            fontColor="gray"
                            fontSize={pallet.general.size12}
                            icon=""
                            title="ACESSAR PERFIL TSE"
                        />
                    </Grid>

                    {/* Content */}
                    {props.parlamentar?.perfil ? (
                        <div
                            ref={conteudoRef}
                            onClick={() => setExpandido(true)}
                            style={{
                                maxHeight: expandido ? 'none' : '315px',
                                overflow: expandido ? 'visible' : 'auto',
                                cursor: expandido ? 'default' : 'pointer',
                            }}
                        >
                            <div ref={pdfRef} style={{ background: '#f8f5ff' }}>
                                {!perfilData ? (
                                    <Box p={4}>
                                        <Typography variant="body2" color="textSecondary">Nenhum perfil disponível.</Typography>
                                    </Box>
                                ) : (
                                    <>
                                        {/* Body */}
                                        <Box sx={{ px: isMobile ? 2 : 4, py: 3 }}>
                                            <Box sx={{ columnCount: { xs: 1, md: 2 }, columnGap: 2 }}>

                                                    {/* Contatos */}
                                                    {(perfilData.contatos?.diretos?.length > 0 || perfilData.contatos?.indiretos?.length > 0) && (
                                                        <SectionCard icon={<ContactPhoneIcon fontSize="small" />} title="Contatos">
                                                            {perfilData.contatos?.diretos?.length > 0 && (
                                                                <Box mb={perfilData.contatos?.indiretos?.length > 0 ? 2 : 0}>
                                                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.medium, textTransform: 'uppercase', mb: 1, letterSpacing: '0.8px' }}>
                                                                        Diretos
                                                                    </Typography>
                                                                    {perfilData.contatos.diretos.map((c, i) => (
                                                                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 0.9 }}>
                                                                            <Box sx={{ color: C.medium, mt: 0.1 }}>{getContactIcon(c.tipo)}</Box>
                                                                            <Box>
                                                                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', lineHeight: 1 }}>{c.tipo}</Typography>
                                                                                <Typography sx={{ fontSize: '0.85rem', color: '#333' }}>{c.detalhe}</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                            {perfilData.contatos?.indiretos?.length > 0 && (
                                                                <Box>
                                                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', mb: 1, letterSpacing: '0.8px' }}>
                                                                        Indiretos
                                                                    </Typography>
                                                                    {perfilData.contatos.indiretos.map((c, i) => (
                                                                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 0.9 }}>
                                                                            <Box sx={{ color: '#aaa', mt: 0.1 }}>{getContactIcon(c.tipo)}</Box>
                                                                            <Box>
                                                                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', lineHeight: 1 }}>{c.tipo}</Typography>
                                                                                <Typography sx={{ fontSize: '0.85rem', color: '#555' }}>{c.detalhe}</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </SectionCard>
                                                    )}

                                                    {/* Histórico Político */}
                                                    {perfilData.historico_politico?.length > 0 && (
                                                        <SectionCard icon={<HistoryIcon fontSize="small" />} title="Histórico Político">
                                                            <Box sx={{ position: 'relative', pl: 1 }}>
                                                                <Box sx={{ position: 'absolute', left: '15px', top: 8, bottom: 8, width: '2px', backgroundColor: C.lavender }} />
                                                                {[...perfilData.historico_politico].sort((a, b) => b.ano - a.ano).map((h, i) => (
                                                                    <Box key={i} sx={{ position: 'relative', pl: 4, mb: 2 }}>
                                                                        <Box sx={{
                                                                            position: 'absolute', left: 8, top: 6,
                                                                            width: 14, height: 14, borderRadius: '50%',
                                                                            backgroundColor: C.medium, border: `2px solid ${C.lavender}`,
                                                                            zIndex: 1,
                                                                        }} />
                                                                        <Box sx={{ backgroundColor: 'rgba(75,17,150,0.03)', borderRadius: '8px', p: 1.5, border: `1px solid ${C.cardBorder}` }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                                                <Chip
                                                                                    label={String(h.ano)}
                                                                                    size="small"
                                                                                    sx={{ backgroundColor: C.medium, color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                                                                                />
                                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: C.dark }}>{h.cargo}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                                                {h.votos != null && (
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                        <HowToVoteIcon sx={{ fontSize: 13, color: '#888' }} />
                                                                                        <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>{h.votos?.toLocaleString('pt-BR')} votos</Typography>
                                                                                    </Box>
                                                                                )}
                                                                                {h.eleitorado && (
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                        <LocationOnIcon sx={{ fontSize: 13, color: '#888' }} />
                                                                                        <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>{h.eleitorado}</Typography>
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </SectionCard>
                                                    )}

                                                    {/* Projetos */}
                                                    {(mainProjects.length > 0 || (perfilData.projetos?.participacoes?.length ?? 0) > 0) && (
                                                        <SectionCard icon={<FolderSpecialIcon fontSize="small" />} title="Projetos Legislativos">
                                                            {mainProjects.length > 0 && (
                                                                <Box mb={perfilData.projetos?.participacoes?.length > 0 ? 2.5 : 0}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.2 }}>
                                                                        <StarIcon sx={{ fontSize: 14, color: C.medium }} />
                                                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.medium, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Principais</Typography>
                                                                    </Box>
                                                                    {mainProjects.map((p, i) => (
                                                                        <Box key={i} sx={{ mb: 1.5, pl: 1.5, borderLeft: `3px solid ${C.lavender}` }}>
                                                                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>{p.nome}</Typography>
                                                                            <Typography sx={{ fontSize: '0.8rem', color: '#555', mt: 0.3 }}>{p.descricao}</Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                            {perfilData.projetos?.participacoes?.length > 0 && (
                                                                <Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.2 }}>
                                                                        <GroupIcon sx={{ fontSize: 14, color: '#999' }} />
                                                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.8px' }}>ParticipaçÁµes</Typography>
                                                                    </Box>
                                                                    {perfilData.projetos.participacoes.map((p, i) => (
                                                                        <Box key={i} sx={{ mb: 1.5, pl: 1.5, borderLeft: '3px solid #e0e0e0' }}>
                                                                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#444' }}>{p.nome}</Typography>
                                                                            <Typography sx={{ fontSize: '0.8rem', color: '#666', mt: 0.3 }}>{p.descricao}</Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </SectionCard>
                                                    )}

                                                    {/* Ocorrências Jurídicas */}
                                                    {perfilData.ocorrencias_juridicas?.length > 0 && (
                                                        <SectionCard icon={<GavelIcon fontSize="small" />} title="Ocorrências Jurídicas">
                                                            {perfilData.ocorrencias_juridicas.map((o, i) => (
                                                                <Box key={i} sx={{
                                                                    display: 'flex', gap: 1.5, mb: 1.5,
                                                                    p: 1.5, borderRadius: '8px',
                                                                    backgroundColor: 'rgba(230, 81, 0, 0.04)',
                                                                    border: '1px solid rgba(230, 81, 0, 0.18)',
                                                                }}>
                                                                    <WarningAmberIcon sx={{ color: C.amber, fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                                                    <Box>
                                                                        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.amber }}>{o.tipo}</Typography>
                                                                        <Typography sx={{ fontSize: '0.8rem', color: '#555', mt: 0.2 }}>{o.detalhe}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </SectionCard>
                                                    )}
                                                    {/* Formação Profissional */}
                                                    {perfilData.formacao_profissional && (
                                                        <SectionCard icon={<SchoolIcon fontSize="small" />} title="Formação Profissional">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                                <WorkIcon sx={{ color: C.medium, fontSize: 16 }} />
                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: C.dark }}>
                                                                    {perfilData.formacao_profissional.profissao}
                                                                </Typography>
                                                            </Box>
                                                            {perfilData.formacao_profissional.formacoes?.map((f, i) => (
                                                                <Box key={i} sx={{ mb: 1.5, pl: 1.5, borderLeft: `3px solid ${C.lavender}` }}>
                                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.primary }}>{f.curso}</Typography>
                                                                    <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>{f.instituicao}</Typography>
                                                                    {f.motivo && (
                                                                        <Typography sx={{ fontSize: '0.73rem', color: '#999', fontStyle: 'italic', mt: 0.2 }}>{f.motivo}</Typography>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </SectionCard>
                                                    )}

                                                    {/* Temas de Atuação */}
                                                    {perfilData.temas_de_atuacao && (
                                                        <SectionCard icon={<PolicyIcon fontSize="small" />} title="Principais Temas de Atuação">
                                                            {perfilData.temas_de_atuacao.defende?.length > 0 && (
                                                                <Box mb={2}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                                                        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: C.green }} />
                                                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Defende</Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                                                        {perfilData.temas_de_atuacao.defende.map((t, i) => (
                                                                            <Chip key={i} label={t} size="small"
                                                                                sx={{ backgroundColor: 'rgba(46,125,50,0.08)', color: C.green, border: '1px solid rgba(46,125,50,0.25)', fontSize: '0.72rem', fontWeight: 600, height: 22 }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                            {perfilData.temas_de_atuacao.ataca?.length > 0 && (
                                                                <Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                                                        <BlockIcon sx={{ fontSize: 14, color: C.red }} />
                                                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.red, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Posiciona-se Contra</Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                                                        {perfilData.temas_de_atuacao.ataca.map((t, i) => (
                                                                            <Chip key={i} label={t} size="small"
                                                                                sx={{ backgroundColor: 'rgba(198,40,40,0.08)', color: C.red, border: '1px solid rgba(198,40,40,0.25)', fontSize: '0.72rem', fontWeight: 600, height: 22 }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </SectionCard>
                                                    )}

                                                    {/* Financiadores */}
                                                    {perfilData.financiadores?.length > 0 && (
                                                        <SectionCard icon={<AttachMoneyIcon fontSize="small" />} title="Financiadores">
                                                            {perfilData.financiadores.map((f, i) => (
                                                                <Box key={i} sx={{ mb: 1.5, p: 1.5, borderRadius: '8px', backgroundColor: 'rgba(59,6,117,0.03)', border: `1px solid ${C.cardBorder}` }}>
                                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>{f.nome}</Typography>
                                                                    <Box sx={{ display: 'flex', gap: 2, mt: 0.4, flexWrap: 'wrap' }}>
                                                                        <Typography sx={{ fontSize: '0.75rem', color: C.green, fontWeight: 700 }}>{f.quantia}</Typography>
                                                                        <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{f.origem}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            ))}
                                                        </SectionCard>
                                                    )}

                                                
                                                    {/* Redes Sociais e Biografia */}
                                                    {(props.parlamentar?.social?.rede1 || props.parlamentar?.social?.rede2 || props.parlamentar?.social?.rede3 || props.parlamentar?.uri) && (
                                                        <SectionCard icon={<LinkIcon fontSize="small" />} title="Redes & Biografia">
                                                            {props.parlamentar?.uri && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: '10px 12px', borderRadius: '8px', backgroundColor: 'rgba(59,6,117,0.04)', border: `1px solid ${C.cardBorder}` }}>
                                                                    <LinkIcon sx={{ color: C.medium, fontSize: 16, flexShrink: 0 }} />
                                                                    <a href={props.parlamentar.uri} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: C.primary, fontSize: '0.82rem', fontWeight: 600 }}>
                                                                        Biografia Completa
                                                                    </a>
                                                                </Box>
                                                            )}
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                {props.parlamentar?.social?.rede1 && (
                                                                    <Box component="a" href={props.parlamentar.social.rede1} target="_blank" rel="noopener noreferrer"
                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.8, borderRadius: '8px', backgroundColor: 'rgba(23,120,242,0.08)', border: '1px solid rgba(23,120,242,0.2)', textDecoration: 'none', color: '#1778F2', fontSize: '0.78rem', fontWeight: 600 }}>
                                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="#1778F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                                                        Facebook
                                                                    </Box>
                                                                )}
                                                                {props.parlamentar?.social?.rede2 && (
                                                                    <Box component="a" href={props.parlamentar.social.rede2} target="_blank" rel="noopener noreferrer"
                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.8, borderRadius: '8px', background: 'linear-gradient(135deg,rgba(254,218,119,.12),rgba(221,42,123,.1),rgba(81,91,212,.1))', border: '1px solid rgba(221,42,123,0.2)', textDecoration: 'none', color: '#C13584', fontSize: '0.78rem', fontWeight: 600 }}>
                                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="url(#ig2)"><defs><linearGradient id="ig2" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FEDA77"/><stop offset="50%" stopColor="#DD2A7B"/><stop offset="100%" stopColor="#515BD4"/></linearGradient></defs><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                                                                        Instagram
                                                                    </Box>
                                                                )}
                                                                {props.parlamentar?.social?.rede3 && (
                                                                    <Box component="a" href={`https://x.com/${props.parlamentar.social.rede3}`} target="_blank" rel="noopener noreferrer"
                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.8, borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.12)', textDecoration: 'none', color: '#111', fontSize: '0.78rem', fontWeight: 600 }}>
                                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="#111"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.262 5.636 5.902-5.636zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                                                        X (Twitter)
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </SectionCard>
                                                    )}

                                            </Box>

                                            {/* Tendências de Votação "” full width */}
                                            {perfilData.tendencias_votacao && (
                                                <Box sx={{ backgroundColor: C.cardBg, borderRadius: '12px', boxShadow: C.cardShadow, border: `1px solid ${C.cardBorder}`, overflow: 'hidden', mt: 2, mb: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, backgroundColor: C.headerBg, borderBottom: `1px solid ${C.cardBorder}` }}>
                                                        <Box sx={{ color: C.medium, display: 'flex' }}><AssessmentIcon fontSize="small" /></Box>
                                                        <Typography sx={{ fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.72rem' }}>
                                                            Posicionamento Histórico
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ p: 2.5 }}>
                                                        {perfilData.tendencias_votacao.resumo && (
                                                            <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(75,17,150,0.04)', borderRadius: '8px', border: `1px solid ${C.cardBorder}` }}>
                                                                <Typography sx={{ fontSize: '0.85rem', color: '#444', fontStyle: 'italic', lineHeight: 1.6 }}>
                                                                    "{perfilData.tendencias_votacao.resumo}"
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Filtrar por tema ou palavra"
                                                            value={votosFiltro}
                                                            onChange={(e) => setVotosFiltro(e.target.value)}
                                                            sx={{ mb: 2.5 }}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon sx={{ color: C.medium, fontSize: 18 }} />
                                                                    </InputAdornment>
                                                                ),
                                                                sx: { fontSize: '0.82rem', borderRadius: '8px' },
                                                            }}
                                                        />
                                                        {(() => {
                                                            const tv = perfilData.tendencias_votacao;
                                                            if (!tv) return null;
                                                            const marca = (arr: TendenciaItem[] | undefined, categoria: 'favoravel' | 'contrario' | 'autoria' | 'relatoria') =>
                                                                (arr || []).map((x) => ({ ...x, categoria }));
                                                            const todos = [
                                                                ...marca(tv.favoravel, 'favoravel'),
                                                                ...marca(tv.contrario, 'contrario'),
                                                                ...marca(tv.autorias, 'autoria'),
                                                                ...marca(tv.relatorias, 'relatoria'),
                                                            ];
                                                            const counts = {
                                                                favoravel: (tv.favoravel || []).length,
                                                                contrario: (tv.contrario || []).length,
                                                                autoria: (tv.autorias || []).length,
                                                                relatoria: (tv.relatorias || []).length,
                                                            };
                                                            let lista = filtroCategoria === 'todos' ? todos : todos.filter((x) => x.categoria === filtroCategoria);
                                                            const q = normalizar(votosFiltro.trim());
                                                            if (q) lista = lista.filter((x) => normalizar(x.tema || x.titulo || '').includes(q));
                                                            const chips: Array<[typeof filtroCategoria, string, string?]> = [
                                                                ['todos', `Todos (${todos.length})`, undefined],
                                                                ['favoravel', `Favoráveis (${counts.favoravel})`, C.green],
                                                                ['contrario', `Contrários (${counts.contrario})`, C.red],
                                                                ['autoria', `Autorias (${counts.autoria})`, C.primary],
                                                                ['relatoria', `Relatorias (${counts.relatoria})`, '#1565C0'],
                                                            ];
                                                            return (
                                                                <>
                                                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                                        {chips.map(([val, label, cor]) => {
                                                                            const ativo = filtroCategoria === val;
                                                                            return (
                                                                                <Chip key={val} label={label} size="small" clickable
                                                                                    onClick={() => setFiltroCategoria(val)}
                                                                                    variant={ativo ? 'filled' : 'outlined'}
                                                                                    sx={{
                                                                                        fontSize: '0.7rem', fontWeight: 600,
                                                                                        color: ativo ? '#fff' : (cor || C.medium),
                                                                                        backgroundColor: ativo ? (cor || C.medium) : 'transparent',
                                                                                        borderColor: cor || C.medium,
                                                                                    }} />
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                    <Box sx={{ maxHeight: 460, overflowY: 'auto', pr: 0.5 }}>
                                                                        {lista.map((item, i) => {
                                                                            const cat = item.categoria;
                                                                            const cor = cat === 'favoravel' ? C.green : cat === 'contrario' ? C.red : cat === 'relatoria' ? '#1565C0' : C.primary;
                                                                            const bg = cat === 'favoravel' ? 'rgba(46,125,50,0.06)' : cat === 'contrario' ? 'rgba(211,47,47,0.06)' : cat === 'relatoria' ? 'rgba(21,101,192,0.06)' : 'rgba(59,6,117,0.06)';
                                                                            const bd = cat === 'favoravel' ? 'rgba(46,125,50,0.18)' : cat === 'contrario' ? 'rgba(211,47,47,0.18)' : cat === 'relatoria' ? 'rgba(21,101,192,0.18)' : 'rgba(59,6,117,0.18)';
                                                                            const icon = cat === 'favoravel'
                                                                                ? <ThumbUpIcon sx={{ color: cor, fontSize: 15, mt: '2px', flexShrink: 0 }} />
                                                                                : cat === 'contrario'
                                                                                    ? <ThumbDownIcon sx={{ color: cor, fontSize: 15, mt: '2px', flexShrink: 0 }} />
                                                                                    : cat === 'relatoria'
                                                                                        ? <GavelIcon sx={{ color: cor, fontSize: 15, mt: '2px', flexShrink: 0 }} />
                                                                                        : <StarIcon sx={{ color: cor, fontSize: 15, mt: '2px', flexShrink: 0 }} />;
                                                                            return (
                                                                                <Box key={i} sx={{ mb: 1, p: '6px 10px', backgroundColor: bg, borderRadius: '6px', border: `1px solid ${bd}`, display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
                                                                                    {icon}
                                                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                                        <Typography sx={{ fontSize: '0.76rem', color: '#333', lineHeight: 1.4 }}>
                                                                                            {item.tema || item.titulo}
                                                                                        </Typography>
                                                                                        {cat === 'autoria'
                                                                                            ? <Typography sx={{ fontSize: '0.66rem', color: C.primary, fontWeight: 700, mt: 0.3 }}>Autoria própria</Typography>
                                                                                            : cat === 'relatoria'
                                                                                                ? <Typography sx={{ fontSize: '0.66rem', color: '#1565C0', fontWeight: 700, mt: 0.3 }}>Relator{item.comissao ? ` · ${item.comissao}` : ''}</Typography>
                                                                                                : renderAutoria(item)}
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                        {lista.length === 0 && (
                                                                            <Typography sx={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic', py: 1 }}>
                                                                                Nenhum item corresponde ao filtro.
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </>
                                                            );
                                                        })()}
                                                    </Box>
                                                </Box>
                                            )}
                                            {renderRede()}
                                        </Box>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <Grid style={{ textAlign: 'center', padding: '30px' }}>
                            <EmptyError title="Ops!" description="Clique no botão Processar Perfil IA" />
                        </Grid>
                    )}
                </TabPanel>
            </Paper>
        </div>
    );
};

export default ParlamentarCarouselTabsPerfil;
